import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { caseFileAPI, bakisAPI } from '../api/api';
import './MuvekkilLoglari.css';

/**
 * Müvekkil Logları
 * ----------------
 * Hangi müvekkil hangi gün giriş yapmış, hangi belgeye bakmış — tek ekranda.
 *
 * Yapı: GÜN → MÜVEKKİL (föy + ad) → hareketler.
 * Bugünün sekmesi açık gelir, önceki günler kapalıdır; tıklayınca açılır.
 * Aynı şekilde müvekkil satırları da kapalı gelir, tıklayınca hareketleri açılır.
 *
 * Bildirim: "son bakış" anı tarayıcıda saklanır; o andan sonraki kayıtlar
 * yeni sayılır. Sayfa açıldığında an güncellenir ve rozet sıfırlanır.
 */

export const SON_BAKIS_ANAHTARI = 'muvekkilLoglariSonBakis';

const iki = (n) => String(n).padStart(2, '0');

const saat = (d) => {
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return '';
  return `${iki(t.getHours())}:${iki(t.getMinutes())}`;
};

const gunAnahtari = (d) => {
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return 'bilinmiyor';
  return `${t.getFullYear()}-${iki(t.getMonth() + 1)}-${iki(t.getDate())}`;
};

const gunBasligi = (anahtar) => {
  if (anahtar === 'bilinmiyor') return 'Tarihi bilinmeyen';
  const [y, a, g] = anahtar.split('-').map(Number);
  const t = new Date(y, a - 1, g);
  const bugun = new Date(); bugun.setHours(0, 0, 0, 0);
  const fark = Math.round((bugun - t) / 86400000);
  if (fark === 0) return 'Bugün';
  if (fark === 1) return 'Dün';
  return t.toLocaleDateString('tr-TR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
};

const bugununAnahtari = () => gunAnahtari(new Date());

const logSinifi = (islem) => {
  switch (islem) {
    case 'Giris': return 'ml-giris';
    case 'GirisBasarisiz':
    case 'GirisDurduruldu': return 'ml-hata';
    case 'Cikis': return 'ml-cikis';
    case 'BelgeGoruntuleme': return 'ml-inceleme';
    case 'BelgeIndirme': return 'ml-indirme';
    case 'BelgeYukleme': return 'ml-yukleme';
    default: return 'ml-diger';
  }
};

const cihazAdi = (ua) => {
  if (!ua) return null;
  const s = ua.toLowerCase();
  if (s.includes('iphone')) return 'iPhone';
  if (s.includes('ipad')) return 'iPad';
  if (s.includes('android')) return 'Android';
  if (s.includes('macintosh') || s.includes('mac os')) return 'Mac';
  if (s.includes('windows')) return 'Windows';
  return 'Diğer';
};

/** Kayıtları gün → müvekkil şeklinde gruplar. Sıra korunur (yeniden eskiye). */
const grupla = (kayitlar) => {
  const gunler = [];
  const gunDizini = new Map();

  for (const k of kayitlar) {
    const gAnahtar = gunAnahtari(k.tarih);
    if (!gunDizini.has(gAnahtar)) {
      const gun = { anahtar: gAnahtar, baslik: gunBasligi(gAnahtar), muvekkiller: [], dizin: new Map(), adet: 0 };
      gunDizini.set(gAnahtar, gun);
      gunler.push(gun);
    }
    const gun = gunDizini.get(gAnahtar);
    gun.adet += 1;

    // Müvekkil anahtarı: föy varsa föy, yoksa TC (giriş/çıkış kayıtları)
    const mAnahtar = k.foyNo != null ? `f${k.foyNo}` : `t${k.muvekkilTC}`;
    if (!gun.dizin.has(mAnahtar)) {
      const m = {
        anahtar: `${gAnahtar}|${mAnahtar}`,
        foyNo: k.foyNo,
        muvekkil: k.muvekkil || 'Bilinmeyen müvekkil',
        caseFileId: k.caseFileId,
        kayitlar: [],
      };
      gun.dizin.set(mAnahtar, m);
      gun.muvekkiller.push(m);
    }
    gun.dizin.get(mAnahtar).kayitlar.push(k);
  }

  return gunler;
};

const MuvekkilLoglari = () => {
  const navigate = useNavigate();
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [gunSayisi, setGunSayisi] = useState(30);

  // Kapalı olanları tutuyoruz: bugün varsayılan AÇIK, diğerleri kapalı.
  const [kapaliGunler, setKapaliGunler] = useState(null);
  const [acikMuvekkiller, setAcikMuvekkiller] = useState(() => new Set());

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    setHata(null);
    try {
      // Son bakış artık sunucuda tutuluyor; cihaz değiştirince ya da
      // önbellek temizlenince rozet sıfırlanmasın diye.
      let sonBakis = null;
      try {
        const b = await bakisAPI.getir();
        sonBakis = b?.muvekkilLog ?? null;
      } catch (_) { /* okunamazsa hepsi yeni sayılır */ }

      const d = await caseFileAPI.muvekkilTumLoglari(gunSayisi, sonBakis);
      setVeri(d);

      // Sayfa görüldü: bildirim sıfırlansın.
      try { await bakisAPI.isaretle('muvekkilLog'); } catch (_) { /* yoksay */ }
    } catch (err) {
      setHata(err?.response?.status === 403
        ? 'Bu ekrana erişim yetkiniz yok.'
        : (err?.response?.data?.message || 'Kayıtlar alınamadı.'));
    } finally {
      setYukleniyor(false);
    }
  }, [gunSayisi]);

  useEffect(() => { yukle(); }, [yukle]);

  const gunler = useMemo(() => grupla(veri?.kayitlar || []), [veri]);

  // İlk yüklemede: bugün açık, diğer günler kapalı.
  useEffect(() => {
    if (kapaliGunler !== null || gunler.length === 0) return;
    const bugun = bugununAnahtari();
    setKapaliGunler(new Set(gunler.filter((g) => g.anahtar !== bugun).map((g) => g.anahtar)));
  }, [gunler, kapaliGunler]);

  const gunAcKapa = (anahtar) => {
    setKapaliGunler((eski) => {
      const yeni = new Set(eski || []);
      if (yeni.has(anahtar)) yeni.delete(anahtar); else yeni.add(anahtar);
      return yeni;
    });
  };

  const muvekkilAcKapa = (anahtar) => {
    setAcikMuvekkiller((eski) => {
      const yeni = new Set(eski);
      if (yeni.has(anahtar)) yeni.delete(anahtar); else yeni.add(anahtar);
      return yeni;
    });
  };

  return (
    <div className="ml-sayfa">
      <div className="ml-baslik">
        <div>
          <h1>Müvekkil Logları</h1>
          <p>Müvekkillerin giriş ve belge hareketleri — gün gün, müvekkil müvekkil.</p>
        </div>
        <div className="ml-baslik-sag">
          <select
            className="form-control ml-aralik"
            value={gunSayisi}
            onChange={(e) => { setGunSayisi(Number(e.target.value)); setKapaliGunler(null); }}
          >
            <option value={7}>Son 7 gün</option>
            <option value={30}>Son 30 gün</option>
            <option value={90}>Son 90 gün</option>
          </select>
          <button type="button" className="btn btn-secondary btn-sm" onClick={yukle}>
            Yenile
          </button>
        </div>
      </div>

      {yukleniyor && <div className="ml-bilgi">Yükleniyor…</div>}
      {hata && <div className="ml-uyari">{hata}</div>}

      {!yukleniyor && !hata && gunler.length === 0 && (
        <div className="ml-bilgi">
          Seçilen aralıkta müvekkil hareketi bulunmuyor.
        </div>
      )}

      {!yukleniyor && !hata && gunler.map((gun) => {
        const kapali = (kapaliGunler || new Set()).has(gun.anahtar);
        return (
          <section key={gun.anahtar} className="ml-gun">
            <button
              type="button"
              className={`ml-gun-baslik ${kapali ? 'kapali' : ''}`}
              onClick={() => gunAcKapa(gun.anahtar)}
              aria-expanded={!kapali}
            >
              <span className="ml-ok" aria-hidden="true">▾</span>
              <span className="ml-gun-ad">{gun.baslik}</span>
              <span className="ml-sayac">{gun.muvekkiller.length} müvekkil</span>
              <span className="ml-sayac ml-sayac-koyu">{gun.adet} hareket</span>
            </button>

            {!kapali && (
              <div className="ml-muvekkiller">
                {gun.muvekkiller.map((m) => {
                  const acik = acikMuvekkiller.has(m.anahtar);
                  const inceleme = m.kayitlar.filter((k) =>
                    k.islem === 'BelgeGoruntuleme' || k.islem === 'BelgeIndirme').length;
                  const giris = m.kayitlar.filter((k) => k.islem === 'Giris').length;

                  return (
                    <div key={m.anahtar} className={`ml-muvekkil ${acik ? 'acik' : ''}`}>
                      <button
                        type="button"
                        className="ml-muvekkil-baslik"
                        onClick={() => muvekkilAcKapa(m.anahtar)}
                        aria-expanded={acik}
                      >
                        <span className="ml-ok" aria-hidden="true">▾</span>
                        {m.foyNo != null && <span className="ml-foy">Föy {m.foyNo}</span>}
                        <span className="ml-ad">{m.muvekkil}</span>
                        <span className="ml-ozet">
                          {giris > 0 && <em className="ml-rozet ml-rozet-giris">{giris} giriş</em>}
                          {inceleme > 0 && <em className="ml-rozet ml-rozet-inceleme">{inceleme} belge</em>}
                        </span>
                      </button>

                      {acik && (
                        <div className="ml-hareketler">
                          {m.caseFileId && (
                            <button
                              type="button"
                              className="ml-dosyaya-git"
                              onClick={() => navigate(`/case-files/${m.caseFileId}`)}
                            >
                              Dosyayı aç →
                            </button>
                          )}
                          <ul>
                            {m.kayitlar.map((k) => (
                              <li key={k.id} className={logSinifi(k.islem)}>
                                <div className="ml-hareket-satir">
                                  <strong>{k.etiket}</strong>
                                  <span className="ml-saat">{saat(k.tarih)}</span>
                                </div>
                                {(k.belgeAdi || k.aciklama) && (
                                  <p className="ml-hareket-aciklama">{k.belgeAdi || k.aciklama}</p>
                                )}
                                {(k.ip || k.cihaz) && (
                                  <p className="ml-hareket-kaynak">
                                    {cihazAdi(k.cihaz)}{k.ip ? ` · ${k.ip}` : ''}
                                  </p>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default MuvekkilLoglari;
